// Global download/action toast system.
//
// Shows a non-blocking notification in the bottom-right while a long-
// running action (PDF generation, packet build, etc.) is in flight.
// Auto-dismisses on success, holds on error so the user sees what
// happened. Used by the FloatingDock action menu items so a click on
// "Generate PDF" gives instant feedback instead of feeling laggy.
//
// Usage:
//   showDownloadToast({ label: "Generating client summary", url: "/api/...", filename: "summary.pdf" });
//   showDownloadToast({ label: "Building expungement packet", run: async () => { ... } });
//
// The Toaster component is mounted once at the app root (App.tsx).

import { useEffect, useState } from "react";

// ─── Types ───────────────────────────────────────────────────────────────

interface ToastItem {
  id: string;
  label: string;
  state: "loading" | "success" | "error";
  detail?: string;
  // Wall-clock when the toast was created. Used to enforce a minimum
  // visible duration so super-fast actions don't flicker invisibly.
  createdAt: number;
}

type Listener = (items: ToastItem[]) => void;

const items: ToastItem[] = [];
const listeners = new Set<Listener>();
const MIN_VISIBLE_MS = 600;
const SUCCESS_LINGER_MS = 1800;
const ERROR_LINGER_MS = 6000;

function emit(): void {
  for (const l of listeners) l([...items]);
}

function newId(): string {
  return `t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

function add(t: ToastItem): void {
  items.push(t);
  emit();
}

function update(id: string, patch: Partial<ToastItem>): void {
  const i = items.findIndex((x) => x.id === id);
  if (i < 0) return;
  items[i] = { ...items[i]!, ...patch };
  emit();
}

function dismiss(id: string): void {
  const i = items.findIndex((x) => x.id === id);
  if (i < 0) return;
  items.splice(i, 1);
  emit();
}

function lingerThenDismiss(id: string, ms: number): void {
  const item = items.find((x) => x.id === id);
  if (!item) return;
  const elapsed = Date.now() - item.createdAt;
  const wait = Math.max(MIN_VISIBLE_MS - elapsed, 0) + ms;
  setTimeout(() => dismiss(id), wait);
}

// ─── Public API ──────────────────────────────────────────────────────────

interface DownloadOptions {
  /** Visible label, e.g. "Generating client summary". */
  label: string;
  /** URL to fetch. Response is treated as a file download. */
  url: string;
  /** Override filename. Defaults to Content-Disposition or URL tail. */
  filename?: string;
  /** Open the file in a new tab instead of downloading. Used for HTML
   *  previews where the response isn't an attachment. */
  inline?: boolean;
}

/**
 * Fetch a URL with a toast spinner, then trigger a download (or open
 * inline). The toast auto-dismisses on success, holds on error.
 */
export async function showDownloadToast(opts: DownloadOptions): Promise<void> {
  const id = newId();
  add({
    id,
    label: opts.label,
    state: "loading",
    createdAt: Date.now(),
  });

  try {
    const res = await fetch(opts.url, { credentials: "same-origin" });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      update(id, {
        state: "error",
        detail: `Server returned ${res.status}${text ? ` - ${text.slice(0, 80)}` : ""}`,
      });
      lingerThenDismiss(id, ERROR_LINGER_MS);
      return;
    }
    const blob = await res.blob();
    const filename =
      opts.filename ??
      filenameFromContentDisposition(res.headers.get("content-disposition")) ??
      filenameFromUrl(opts.url) ??
      "download";

    triggerDownload(blob, filename, opts.inline === true);

    update(id, {
      state: "success",
      detail: opts.inline ? `Opened ${filename}` : `Downloaded ${filename}`,
    });
    lingerThenDismiss(id, SUCCESS_LINGER_MS);
  } catch (err) {
    update(id, {
      state: "error",
      detail: err instanceof Error ? err.message : String(err),
    });
    lingerThenDismiss(id, ERROR_LINGER_MS);
  }
}

/**
 * Run an arbitrary async function with a toast spinner. Use this for
 * actions that don't fit the simple "fetch a URL → download" model
 * (e.g. multi-step packet builders that already have their own logic).
 */
export async function runWithToast(opts: {
  label: string;
  successDetail?: string;
  run: () => Promise<void>;
}): Promise<void> {
  const id = newId();
  add({
    id,
    label: opts.label,
    state: "loading",
    createdAt: Date.now(),
  });
  try {
    await opts.run();
    update(id, { state: "success", detail: opts.successDetail });
    lingerThenDismiss(id, SUCCESS_LINGER_MS);
  } catch (err) {
    update(id, {
      state: "error",
      detail: err instanceof Error ? err.message : String(err),
    });
    lingerThenDismiss(id, ERROR_LINGER_MS);
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────────

function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  // Match filename="..." or filename*=UTF-8''...
  const m1 = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (m1?.[1]) return decodeURIComponent(m1[1]);
  const m2 = /filename="?([^";]+)"?/i.exec(header);
  if (m2?.[1]) return m2[1];
  return null;
}

function filenameFromUrl(url: string): string | null {
  try {
    const u = new URL(url, window.location.origin);
    const last = u.pathname.split("/").filter(Boolean).pop();
    return last ?? null;
  } catch {
    return null;
  }
}

function triggerDownload(blob: Blob, filename: string, inline: boolean): void {
  const objectUrl = URL.createObjectURL(blob);
  if (inline) {
    window.open(objectUrl, "_blank", "noopener,noreferrer");
    // Revoke after a beat so the new tab can finish loading.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    return;
  }
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}

// ─── React component ─────────────────────────────────────────────────────

export function DownloadToaster(): JSX.Element {
  const [list, setList] = useState<ToastItem[]>([]);
  useEffect(() => {
    const l: Listener = (xs) => setList(xs);
    listeners.add(l);
    l([...items]);
    return () => {
      listeners.delete(l);
    };
  }, []);

  if (list.length === 0) return <></>;
  return (
    <div
      style={{
        position: "fixed",
        right: 24,
        bottom: 24,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        alignItems: "flex-end",
        pointerEvents: "none",
      }}
    >
      {list.map((t) => (
        <ToastCard key={t.id} item={t} onClose={() => dismiss(t.id)} />
      ))}
      <style>{`
        @keyframes dt-rise {
          from { opacity: 0; transform: translateY(12px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes dt-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

function ToastCard({ item, onClose }: { item: ToastItem; onClose: () => void }): JSX.Element {
  const isError = item.state === "error";
  const isSuccess = item.state === "success";
  const accent = isError
    ? "var(--color-rose-hi, #FB7185)"
    : isSuccess
      ? "var(--color-sage-hi, #22C55E)"
      : "var(--color-hero-bg, #A02C44)";
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        pointerEvents: "auto",
        minWidth: 280,
        maxWidth: 380,
        background: "var(--color-bg-card, #1C1F26)",
        border: `1px solid ${accent}`,
        borderRadius: 12,
        padding: "12px 14px",
        boxShadow:
          "0 4px 14px rgba(0, 0, 0, 0.18), 0 18px 36px -12px rgba(0, 0, 0, 0.28)",
        display: "grid",
        gridTemplateColumns: "auto 1fr auto",
        gap: 12,
        alignItems: "center",
        animation: "dt-rise 220ms cubic-bezier(0.16, 1, 0.3, 1)",
        fontFamily: "var(--font-body, Inter, system-ui, sans-serif)",
      }}
    >
      <span
        aria-hidden
        style={{
          width: 24,
          height: 24,
          borderRadius: 999,
          background: `color-mix(in oklab, ${accent} 14%, transparent)`,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: accent,
          flexShrink: 0,
        }}
      >
        {item.state === "loading" && (
          <svg width="14" height="14" viewBox="0 0 14 14" style={{ animation: "dt-spin 0.9s linear infinite" }} aria-hidden="true">
            <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.25" fill="none" />
            <path d="M7 2a5 5 0 0 1 5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none" />
          </svg>
        )}
        {item.state === "success" && (
          <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
            <path d="M3 7.5l3 3L11 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </svg>
        )}
        {item.state === "error" && (
          <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
            <path d="M7 4v3.5M7 10v.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            <circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
          </svg>
        )}
      </span>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            color: "var(--color-text, #F2E9D6)",
            fontSize: 14,
            fontWeight: 600,
            lineHeight: 1.3,
          }}
        >
          {item.label}
        </div>
        {item.detail && (
          <div
            style={{
              color: "var(--color-text-muted, #B8AFA0)",
              fontSize: 12.5,
              fontFamily: "var(--font-mono, ui-monospace, monospace)",
              letterSpacing: "0.02em",
              marginTop: 2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {item.detail}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Dismiss"
        style={{
          width: 24,
          height: 24,
          background: "transparent",
          border: "none",
          color: "var(--color-text-dim, #8B847A)",
          cursor: "pointer",
          padding: 0,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
          <path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
}
